import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, refreshSession, setAccessToken, setUnauthorisedHandler } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    setUnauthorisedHandler(() => setUser(null));
    // silent session restore via refresh cookie
    (async () => {
      try {
        const { data } = await refreshSession();
        setAccessToken(data.accessToken);
        setUser(data.user);
      } catch (e) {
        // no session
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAccessToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setAccessToken(null);
    setUser(null);
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await api.post("/auth/change-password", { currentPassword, newPassword });
    setUser((u) => (u ? { ...u, mustChangePassword: false } : u));
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
  }, []);

  return (
    <AuthContext.Provider value={{ user, booting, login, logout, changePassword, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
